import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { ShortcutKbd } from "@repo/ui/lib/actions/shortcut-kbd";
import type { ShortcutId } from "@repo/ui/lib/actions/shortcuts";
import { Button } from "@repo/ui/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
} from "@repo/ui/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/ui/tooltip";
import {
  createLink,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";

import { boards$ } from "~/stores/boards";
import { createBoard } from "~/stores/boards/boards.actions";
import { datasets$ } from "~/stores/datasets";
import { createDataset } from "~/stores/datasets/datasets.actions";
import { activeThreadId$, newThread, threads$ } from "~/stores/threads";
import { ActiveBoardItem, BoardsList } from "./boards-list";
import {
  ActiveDatasetItem,
  DraftDatasetsList,
  hasDraftDatasets$,
  hasSharedDatasets$,
  PublishedDatasetsList,
} from "./datasets-list";
import { FavoritesList, hasFavorites$ } from "./favorites-list";
import { SharedWithMe } from "./shared-with-me";
import { ActiveThreadItem, ThreadsList } from "./threads-list";

const ButtonLink = createLink(Button);

/**
 * Shared collapsible section header: a label, an optional hover-revealed
 * browse link, a hover-revealed `+` action, and the open/closed chevron. Used
 * by the Drafts, Datasets, and Dashboards groups.
 */
const SectionHeader = ({
  label,
  tooltip,
  onAdd,
  addTooltip,
  browseTo,
  browseTitle,
  browseTooltip,
  browseShortcutId,
}: {
  label: string;
  tooltip?: string;
  onAdd?: () => void;
  addTooltip?: string;
  /** Route for the section's full browse page, rendered next to the `+`. */
  browseTo?: string;
  /** Bolded first line of the browse tooltip; omit for a single-line tooltip. */
  browseTitle?: string;
  browseTooltip?: string;
  /** Shortcut badge shown alongside `browseTooltip`. */
  browseShortcutId?: ShortcutId;
}) => (
  <CollapsibleTrigger
    nativeButton={false}
    render={
      <SidebarGroupLabel className="group/label my-1 flex w-full cursor-pointer select-none items-center hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" />
    }
  >
    {tooltip ? (
      <Tooltip>
        <TooltipTrigger render={<span className="select-none" />}>
          {label}
        </TooltipTrigger>
        <TooltipContent side="right">{tooltip}</TooltipContent>
      </Tooltip>
    ) : (
      label
    )}
    {browseTo && (
      <Tooltip>
        <TooltipTrigger
          render={
            <ButtonLink
              size="icon-xs"
              variant="ghost"
              to={browseTo}
              // The trigger sits inside the collapsible's own trigger, so the
              // click must not also toggle the section open/closed.
              onClick={(e) => e.stopPropagation()}
              className="ml-auto opacity-0 transition-none hover:bg-foreground/15 group-hover/label:opacity-100"
            />
          }
        >
          {/* The glyph fills its viewBox edge to edge, so at the default size
              it reads heavier than the `+` and chevron beside it. */}
          <Icon name="dot-grid" className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className={
            browseTitle
              ? "flex max-w-52 flex-col items-start gap-1"
              : "flex items-center gap-2"
          }
        >
          {browseTitle ? (
            <>
              <span className="flex items-center gap-2 font-medium">
                {browseTitle}
                {browseShortcutId && (
                  <ShortcutKbd shortcutId={browseShortcutId} />
                )}
              </span>
              <span className="text-muted-foreground">{browseTooltip}</span>
            </>
          ) : (
            <>
              {browseTooltip}
              {browseShortcutId && (
                <ShortcutKbd shortcutId={browseShortcutId} />
              )}
            </>
          )}
        </TooltipContent>
      </Tooltip>
    )}
    {onAdd && (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              size="icon-xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              className={`opacity-0 transition-none hover:bg-foreground/15 group-hover/label:opacity-100 ${browseTo ? "" : "ml-auto"}`}
            />
          }
        >
          <Icon name="plus" />
        </TooltipTrigger>
        <TooltipContent side="top">{addTooltip}</TooltipContent>
      </Tooltip>
    )}
    <Icon
      name="chevron-right"
      className={`transition-transform duration-200 group-hover/label:opacity-100 group-data-open/collapsible:rotate-90 group-data-closed/collapsible:opacity-100 ${onAdd ? "opacity-0" : "ml-auto opacity-0"}`}
    />
  </CollapsibleTrigger>
);

export const SidebarHome = () => {
  const navigate = useNavigate();
  const isBoardRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith("/boards/"),
  });
  const isDatasetRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith("/datasets/"),
  });

  // Starts a fresh conversation and opens it full-page.
  const handleNewThread = () => {
    newThread();
    const id = activeThreadId$.get();
    if (id) navigate({ to: "/thread/$threadId", params: { threadId: id } });
  };

  const isDatasetsEmpty = useValue(() => Object.keys(datasets$).length === 0);

  const activeDatasetId = useParams({
    strict: false,
    select: (p) => p.datasetId,
  });
  const activeBoardId = useParams({
    strict: false,
    select: (p) => p.boardId,
  });
  const activeThreadId = useParams({
    strict: false,
    select: (p) => p.threadId,
  });

  const hasActiveDataset = useValue(() =>
    activeDatasetId ? datasets$[activeDatasetId].id.get() !== undefined : false,
  );
  const activeDatasetIsPrivate = useValue(() =>
    activeDatasetId ? datasets$[activeDatasetId].private.get() === true : false,
  );
  const activeDatasetIsFavorite = useValue(() =>
    activeDatasetId
      ? datasets$[activeDatasetId].favorite.get() === true
      : false,
  );
  const hasActiveBoard = useValue(() =>
    activeBoardId ? boards$[activeBoardId].id.get() !== undefined : false,
  );
  const activeBoardIsFavorite = useValue(() =>
    activeBoardId ? boards$[activeBoardId].favorite.get() === true : false,
  );
  const activeThreadIsFavorite = useValue(() =>
    activeThreadId
      ? threads$[activeThreadId].metadata.favorite.get() === true
      : false,
  );

  const hasFavorites = useValue(hasFavorites$);
  const hasDrafts = useValue(hasDraftDatasets$);
  const hasShared = useValue(hasSharedDatasets$);

  const context = isBoardRoute ? "board" : isDatasetRoute ? "dataset" : "other";

  const showDrafts = hasDrafts;
  const showDatasets = hasShared || isDatasetsEmpty;

  return (
    <SidebarGroup key={context}>
      {hasFavorites && (
        <Collapsible
          defaultOpen
          className="group/collapsible group-data-[collapsible=icon]:hidden"
        >
          <SectionHeader
            label="Favorites"
            tooltip="Boards, datasets, and threads you've starred for quick access."
          />
          <SidebarMenu className="hidden group-data-closed/collapsible:block">
            {activeBoardId && hasActiveBoard && activeBoardIsFavorite && (
              <ActiveBoardItem id={activeBoardId} />
            )}
            {activeDatasetId && hasActiveDataset && activeDatasetIsFavorite && (
              <ActiveDatasetItem id={activeDatasetId} />
            )}
            {activeThreadId && activeThreadIsFavorite && (
              <ActiveThreadItem id={activeThreadId} />
            )}
          </SidebarMenu>
          <CollapsibleContent>
            <FavoritesList />
          </CollapsibleContent>
        </Collapsible>
      )}
      {showDrafts && (
        <Collapsible
          defaultOpen={!isBoardRoute}
          className="group/collapsible group-data-[collapsible=icon]:hidden"
        >
          <SectionHeader
            label="Drafts"
            tooltip="Your private datasets that haven't been published yet."
            onAdd={createDataset}
            addTooltip="New dataset"
          />
          {activeDatasetId &&
            hasActiveDataset &&
            activeDatasetIsPrivate &&
            !activeDatasetIsFavorite && (
              <SidebarMenu className="hidden group-data-closed/collapsible:block">
                <ActiveDatasetItem id={activeDatasetId} />
              </SidebarMenu>
            )}
          <CollapsibleContent>
            <DraftDatasetsList />
          </CollapsibleContent>
        </Collapsible>
      )}
      {showDatasets && (
        <Collapsible
          defaultOpen={!isBoardRoute}
          className="group/collapsible group-data-[collapsible=icon]:hidden"
        >
          <SectionHeader
            label="Datasets"
            tooltip="Published datasets you can query and build boards from."
            onAdd={createDataset}
            addTooltip="New dataset"
            browseTo="/datasets"
            browseTitle="Catalog"
            browseTooltip="Browse and organize all datasets."
            browseShortcutId="browse-datasets"
          />
          {activeDatasetId &&
            hasActiveDataset &&
            !activeDatasetIsPrivate &&
            !activeDatasetIsFavorite && (
              <SidebarMenu className="hidden group-data-closed/collapsible:block">
                <ActiveDatasetItem id={activeDatasetId} />
              </SidebarMenu>
            )}
          <CollapsibleContent>
            {isDatasetsEmpty ? (
              <div className="px-2 pb-2">
                <p className="pb-2 text-sidebar-foreground/70 text-xs">
                  Create a dataset to start querying your datasources.
                </p>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={createDataset}
                >
                  <Icon name="plus" className="text-muted-foreground" />
                  Create a dataset
                </Button>
              </div>
            ) : (
              <PublishedDatasetsList />
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
      <Collapsible
        defaultOpen
        className="group/collapsible group-data-[collapsible=icon]:hidden"
      >
        <SectionHeader
          label="Dashboards"
          tooltip="Boards that visualize your datasets."
          onAdd={createBoard}
          addTooltip="New dashboard"
        />
        {activeBoardId && hasActiveBoard && !activeBoardIsFavorite && (
          <SidebarMenu className="hidden group-data-closed/collapsible:block">
            <ActiveBoardItem id={activeBoardId} />
          </SidebarMenu>
        )}
        <CollapsibleContent>
          <BoardsList />
        </CollapsibleContent>
      </Collapsible>
      <Collapsible
        defaultOpen
        className="group/collapsible group-data-[collapsible=icon]:hidden"
      >
        <SectionHeader
          label="Threads"
          tooltip="Your AI chat conversations."
          onAdd={handleNewThread}
          addTooltip="New thread"
        />
        {activeThreadId && !activeThreadIsFavorite && (
          <SidebarMenu className="hidden group-data-closed/collapsible:block">
            <ActiveThreadItem id={activeThreadId} />
          </SidebarMenu>
        )}
        <CollapsibleContent>
          <ThreadsList />
        </CollapsibleContent>
      </Collapsible>
      <SharedWithMe />
    </SidebarGroup>
  );
};
